import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'

import {
  InviteOrganizationUserButton,
  OrganizationInvitesTable,
} from '@/features/organization'
import {
  useCancelInvite,
  useOrganizationInvites,
  useResendInvite,
} from '@/features/organization/services/organization.convex'
import {
  AuthGuard,
  useCurrentOrganizationId,
  useMyConvexOrganizationId,
} from '@/features/auth'

export const Route = createFileRoute('/(dashboard)/organization/invites/')({
  component: RouteComponent,
  beforeLoad: () => ({
    title: 'Organization Invites',
    breadcrumbs: [{ title: 'Organization' }, { title: 'Invites' }],
  }),
})

function RouteComponent() {
  // Legacy membership ID is what AuthGuard expects; Convex queries need the
  // actual organizations._id from the Convex profile sync.
  const legacyOrgId = useCurrentOrganizationId()
  const convexOrgId = useMyConvexOrganizationId()

  const { data: invites, isLoading } = useOrganizationInvites(convexOrgId)

  const resendInviteMutation = useResendInvite()
  const cancelInviteMutation = useCancelInvite()

  const handleResendInvite = async (inviteId: string) => {
    try {
      await resendInviteMutation.mutateAsync(inviteId)
      toast.success('Invitation resent successfully')
    } catch (error) {
      toast.error((error as Error).message || 'Failed to resend invite')
    }
  }

  const handleDeleteInvite = async (inviteId: string) => {
    try {
      await cancelInviteMutation.mutateAsync(inviteId)
      toast.success('Invite deleted successfully!')
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete invite')
    }
  }

  return (
    <AuthGuard
      requiredOrganizationRole={{
        organizationId: legacyOrgId || '',
        role: ['admin', 'manager'],
      }}
    >
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Organization Invites
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              View and manage organization invitations
            </p>
          </div>
          <InviteOrganizationUserButton
            onSuccess={() => {
              toast.success('Invitation sent successfully!')
              // Convex live-updates the invites list automatically.
            }}
          />
        </div>

        {/* Table Section */}
        <OrganizationInvitesTable
          invites={invites}
          isLoading={isLoading}
          onResendInvite={handleResendInvite}
          onDeleteInvite={handleDeleteInvite}
          isResending={resendInviteMutation.isPending}
          isDeleting={cancelInviteMutation.isPending}
        />
      </div>
    </AuthGuard>
  )
}
